/**
 * Module 6 — turning "what's in the fridge" into "what could I cook".
 *
 * The ranking exists to serve the app's actual purpose: not to find the nicest
 * recipe, but to find the one that uses the thing about to go off. A dish you
 * can *almost* make from dying spinach beats a dish you can fully make from
 * things with three weeks left.
 */

import { daysUntilExpiry } from "../food/expiry";
import type { FoodItem } from "../food/types";
import { LEVEL_RANK } from "../settings/types";
import type { Settings } from "../settings/types";
import { findMatch, isStaple } from "./matchIngredients";
import { proteinDensity } from "./types";
import type { Recipe } from "./types";

/**
 * A recipe needs at least half its ingredients to hand before it's worth
 * suggesting. Below that it stops being a suggestion and becomes a shopping
 * list.
 */
const MIN_COVERAGE = 0.5;

export interface Suggestion {
  recipe: Recipe;
  /** The items you already have, in the recipe's own ingredient order. */
  have: FoodItem[];
  /** Ingredient names you'd need to buy. */
  missing: string[];
  /**
   * Days until the soonest-expiring matched item runs out — the reason this
   * recipe is being suggested now rather than later. Null when nothing it uses
   * is time-sensitive.
   */
  urgentInDays: number | null;
  /** The item behind that number, so the UI can name it. */
  urgentItem: FoodItem | null;
}

/**
 * Urgency, in bands rather than exact days.
 *
 * Sorting on the raw day count meant a recipe using something with 4 days left
 * always beat one with 5, which is a distinction nobody cares about — and it
 * left no room for anything else to matter. Banding keeps the app's actual
 * purpose first (use the thing that's dying) while letting the macros decide
 * the order *within* a band, which is where the eating goal becomes real.
 */
function urgencyBand(days: number | null): number {
  if (days === null) return 4;
  if (days <= 0) return 0; // today or already past
  if (days <= 2) return 1;
  if (days <= 5) return 2;
  if (days <= 10) return 3;
  return 4;
}

/** Vegan suits a vegetarian; the reverse is not true. */
function dietAllows(recipe: Recipe, settings: Settings): boolean {
  if (settings.diet === "anything") return true;
  if (settings.diet === "vegan") return recipe.diet === "vegan";
  return recipe.diet === "vegetarian" || recipe.diet === "vegan";
}

function suits(recipe: Recipe, settings: Settings): boolean {
  const skillOk =
    LEVEL_RANK[recipe.difficulty] <= LEVEL_RANK[settings.cookingExperience];
  return dietAllows(recipe, settings) && skillOk;
}

/**
 * Higher is better, for whichever goal is set. This is the whole of what
 * bulk / cut / maintain does — it never hides a recipe, it reorders.
 */
function goalScore(recipe: Recipe, settings: Settings): number {
  switch (settings.eating) {
    // Most protein for the fewest calories.
    case "cut":
      return proteinDensity(recipe);
    // Calories, but not empty ones — a 900-calorie plate of rice is not what
    // anyone means by bulking.
    case "bulk":
      return recipe.calories + recipe.protein * 4;
    // Just protein. The default, and the one that surprises nobody.
    case "maintain":
      return recipe.protein;
  }
}

/**
 * Ranks recipes for what's in the house right now.
 *
 * @param today an ISO calendar date, passed in so every row counts from the
 *              same day and tests can pick one
 */
export function suggestRecipes(
  recipes: Recipe[],
  items: FoodItem[],
  settings: Settings,
  today: string,
  limit = 12
): Suggestion[] {
  const suggestions: Suggestion[] = [];

  for (const recipe of recipes) {
    if (!suits(recipe, settings)) continue;

    const have: FoodItem[] = [];
    const missing: string[] = [];
    /*
     * Ingredients we assume you have without finding them: salt, pepper, oil,
     * water. They count towards coverage but never appear in `missing`.
     *
     * Counted separately rather than pushed into `have` because `have` holds
     * real FoodItems — it is what the urgency scan reads and what "I cooked
     * this" offers to deduct, and a phantom entry there would be a bug in both.
     */
    let assumed = 0;

    for (const ingredient of recipe.ingredients) {
      // Items already spoken for by an earlier ingredient are still eligible:
      // one tin of tomatoes satisfying two lines is a rounding error next to
      // the confusion of a recipe claiming you lack something you can see.
      const match = findMatch(items, ingredient);
      if (match) have.push(match);
      else if (isStaple(ingredient)) assumed += 1;
      else missing.push(ingredient);
    }

    if ((have.length + assumed) / recipe.ingredients.length < MIN_COVERAGE) continue;

    let urgentItem: FoodItem | null = null;
    let urgentInDays: number | null = null;
    for (const item of have) {
      const days = daysUntilExpiry(item, today);
      if (urgentInDays === null || days < urgentInDays) {
        urgentInDays = days;
        urgentItem = item;
      }
    }

    suggestions.push({ recipe, have, missing, urgentInDays, urgentItem });
  }

  return suggestions
    .sort((a, b) => {
      // Band first: what's about to go off still leads, because that is what
      // the app is for.
      const band = urgencyBand(a.urgentInDays) - urgencyBand(b.urgentInDays);
      if (band !== 0) return band;

      // Within a band, the eating goal decides. Higher score first.
      const goal = goalScore(b.recipe, settings) - goalScore(a.recipe, settings);
      if (goal !== 0) return goal;

      // Then whatever needs the least shopping.
      if (a.missing.length !== b.missing.length) return a.missing.length - b.missing.length;

      // Then whatever uses the most of what's already here.
      if (a.have.length !== b.have.length) return b.have.length - a.have.length;

      // Alphabetical last, so the order never wobbles between renders.
      return a.recipe.title.localeCompare(b.recipe.title);
    })
    .slice(0, limit);
}
